// React imports
import React, { useEffect, useState } from "react";

// External library imports
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import HourglassTop from "@mui/icons-material/HourglassTop";
import ModelTraining from "@mui/icons-material/ModelTraining";
import RateReviewIcon from "@mui/icons-material/RateReview";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import dayjs from "dayjs";

// Local imports
import Navbar from "../../components/Page Component/Navbar";
import Footer from "../../components/Page Component/Footer";
import {
  ViewUser,
  Employee_and_manager,
  subordinateDetails,
  getDirectReports,
  managerAssignmentsUrl,
} from "../../Service/Apis";
import EmployeeSkillSet from "../Skill Set/EmployeeSkillSet";
import capitalizeFirstLetter from "../../components/functions/NameFunc";
import { setIsLoading } from "../../store/LoadingSlice";
import { RootState } from "../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../store/actions";
import { EmployeeData, ManagerData, SubordinateData, DirectReport, ManagerAssignmentInfo, ManagedEmployeeAssignment } from "./Types/LandingPageTypes";

const LandingPage: React.FC = () => {
  // 1. useState declarations
  const [employeeData, setEmployeeData] = useState<EmployeeData | null>(null);
  const [managerData, setManagerData] = useState<ManagerData | null>(null);
  const [subordinateData, setSubordinateData] = useState<
    SubordinateData[] | null
  >(null);
  const [globalDirectReports, setGlobalDirectReports] = useState<DirectReport[]>([]);
  const [managerAssignments, setManagerAssignments] = useState<ManagerAssignmentInfo[]>([]);
  const [managedEmployees, setManagedEmployees] = useState<ManagedEmployeeAssignment[]>([]);

  // 2. const variables (hooks, Redux, computed constants)
  const email = localStorage.getItem("email");
  const token = localStorage.getItem("token");
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();
  const roles = useSelector((state: RootState) => state.auth.roles || []);
  // selectedRole is updated by the navbar role switcher via Redux — reactive
  const selectedRole = useSelector((state: RootState) => state.auth.selectedRole || "");
  const isActiveManager = selectedRole.toLowerCase() === "manager";

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Fetch Employee Data
        const employeeResponse = await dispatch2(
          fetchData(`${ViewUser}${email}`)
        );
        setEmployeeData(employeeResponse[0]);

        const empId = employeeResponse[0]?.id;
        if (empId) {
          // Fetch M1/M2 manager assignments for this employee
          try {
            const assignments = await dispatch2(
              fetchData(`${managerAssignmentsUrl}?employee_id=${empId}`)
            );
            if (Array.isArray(assignments)) {
              setManagerAssignments(
                (assignments as any[])
                  .filter((a) => a.is_active && a.manager_name)
                  .map((a) => ({
                    id: a.id,
                    manager_name: a.manager_name,
                    manager_level: a.manager_level,
                    assignment_type: a.assignment_type ?? null,
                    from_date: a.from_date ?? null,
                    till_date: a.till_date ?? null,
                    is_active: a.is_active,
                  }))
              );
            }
          } catch (e) {
            setManagerAssignments([]);
          }

          // Fetch M1/M2 employees that report TO the current user (only when actively using Manager role)
          try {
            if (isActiveManager) {
              const assignments = await dispatch2(
                fetchData(`${managerAssignmentsUrl}?manager_id=${empId}`)
              );
              if (Array.isArray(assignments)) {
                setManagedEmployees(
                  (assignments as any[])
                    .filter((a) => a.is_active && a.employee_name)
                    .map((a) => ({
                      id: a.id,
                      employee_name: a.employee_name,
                      manager_level: a.manager_level,
                      assignment_type: a.assignment_type ?? null,
                      from_date: a.from_date ?? null,
                      till_date: a.till_date ?? null,
                    }))
                );
              }
            }
          } catch (e) {
            setManagedEmployees([]);
          }

          // Global direct reports using actual assigned roles (not selectedRole)
          try {
            const hasManagerRole = (roles || []).some((r: string) => (r || "").toLowerCase() === "manager");
            const isPrivileged = (roles || []).some((r: string) => ["admin", "hr"].includes((r || "").toLowerCase()));

            let list: Array<{ id: string; first_name: string; last_name: string }> = [];

            if (hasManagerRole) {
              const dr = await dispatch2(fetchData(`${getDirectReports}`));
              list = dr?.direct_reports || [];
            } else if (isPrivileged && empId) {
              const dr = await dispatch2(fetchData(`${getDirectReports}?manager_id=${empId}`));
              list = dr?.direct_reports || [];
            } else {
              list = [];
            }

            setGlobalDirectReports(list || []);
          } catch (e) {
            // If forbidden or other error, keep empty and avoid further calls
            setGlobalDirectReports([]);
          }
        }

        // Fetch Manager Data
        const managerResponse = await dispatch2(
          fetchData(Employee_and_manager)
        );

        if (managerResponse.managers && managerResponse.managers.length > 0) {
          // Group projects by manager name
          const managerProjectMap: Record<string, string[]> = {};
          managerResponse.managers.forEach((mgr: { reporting_manager_name?: string; project_name?: string }) => {
            const name = mgr.reporting_manager_name || "No Manager Assigned";
            const project = mgr.project_name || "No Project Assigned";
            if (!managerProjectMap[name]) {
              managerProjectMap[name] = [];
            }
            managerProjectMap[name].push(project);
          });

          // Save as array of {name, projects}
          const managersWithProjects = Object.entries(managerProjectMap).map(
            ([name, projects]) => ({
              name,
              projects,
            })
          );

          setManagerData({
            reporting_managers: managersWithProjects,
            project_names: [], 
          });
        } else {
          setManagerData({
            reporting_managers: [],
            project_names: [],
          });
        }

        // Fetch Subordinate Data
        const subordinateResponse = await dispatch2(
          fetchData(subordinateDetails)
        );
        setSubordinateData(subordinateResponse);
      } catch (error: unknown) {
        // Error fetching data - silently fail to avoid disrupting user experience
      }
    };

    fetchAllData();
  }, [email, roles, selectedRole]);

  // Quick links data
  const quickLinks = [
    {
      id: 1,
      title: "My Reviews",
      path: "/pms/my-reviews",
      description: "Track your performance reviews",
      icon: <RateReviewIcon />,
    },
    {
      id: 2,
      title: "Utilization",
      path: "/utilization",
      description: "Analyze time spent on tasks",
      icon: <HourglassTop />,
    },
    {
      id: 3,
      title: "Update Skills",
      path: "/employee-skill-set",
      description: "Add or modify your skills",
      icon: <ModelTraining />,
    },
    // COMMENTED OUT - Past Appraisals moved to sidebar under Reports
    // {
    //   id: 4,
    //   title: "Past Appraisals",
    //   path: "/historical-appraisal",
    //   description: "Graph record of past appraisals",
    //   icon: <Verified />,
    // },
    // COMMENTED OUT - Previous Responses card hidden
    // {
    //   id: 5,
    //   title: "Previous Responses",
    //   path: "/previousresponses",
    //   description: "View past responses and evaluations",
    //   icon: <ArticleIcon />,
    // },
  ];
  const uniqueManagers =
    managerData && managerData.reporting_managers
      ? Array.from(new Set(managerData.reporting_managers))
      : [];
  return (
    <>
      <Navbar />
      <Box sx={{ mt: 5, mb: 10, backgroundColor: "#f5f7fa", p: 4 }}>
        {/* Personal Details & Reporting Structure - Single Horizontal Card */}
        <Card sx={{ boxShadow: 3, borderRadius: 2, mb: 4 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { md: "flex-start" },
                width: "100%",
                gap: 2,
              }}
            >
              {/* Column 1: Personal Details — Name, Email, Date of Birth, Gender */}
              <Box
                sx={{
                  flex: "0 0 28%",
                  minWidth: 0,
                  borderRight: { md: "1px solid #e0e0e0" },
                  pr: { md: 2 },
                  boxSizing: "border-box",
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: "#007300", mb: 3 }}
                >
                  Personal Details
                </Typography>
                {employeeData ? (
                  <Stack spacing={1.5}>
                    <Typography>
                      Name: <strong>{employeeData.first_name} {employeeData.last_name}</strong>
                    </Typography>
                    <Typography>
                      Email: <strong>{employeeData.email}</strong>
                    </Typography>
                    <Typography>
                      Date of Birth:{" "}
                      <strong>{new Date(employeeData.date_of_birth).toLocaleDateString("en-GB")}</strong>
                    </Typography>
                    <Typography>
                      Gender: <strong>{employeeData.gender}</strong>
                    </Typography>
                  </Stack>
                ) : (
                  <Typography>Loading...</Typography>
                )}
              </Box>

              {/* Column 2: Organization Details — Employee Code, Date of Joining, Designation, Access */}
              <Box
                sx={{
                  flex: "0 0 30%",
                  minWidth: 0,
                  borderRight: { md: "1px solid #e0e0e0" },
                  px: { md: 2 },
                  boxSizing: "border-box",
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: "#007300", mb: 3 }}
                >
                  Organization Details
                </Typography>
                {employeeData ? (
                  <Stack spacing={1.5}>
                    <Typography>
                      Employee Code: <strong>{employeeData.employee_code}</strong>
                    </Typography>
                    <Typography>
                      Designation: <strong>{employeeData.designation}</strong>
                    </Typography>
                    <Typography>
                      Date of Joining:{" "}
                      <strong>{new Date(employeeData.date_of_joining).toLocaleDateString("en-GB")}</strong>
                    </Typography>
                    <Typography>
                      Access: <strong>{employeeData.roles.join(", ")}</strong>
                    </Typography>
                  </Stack>
                ) : (
                  <Typography>Loading...</Typography>
                )}
              </Box>

              {/* Column 3: Reporting Structure */}
              <Box
                sx={{
                  flex: "1 1 0",
                  minWidth: 0,
                  pl: { md: 2 },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: "#007300", mb: 3 }}
                >
                  Reporting Structure
                </Typography>
                {managerData || employeeData ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                    }}
                  >
                    {(() => {
                      const typeLabel: Record<string, string> = {
                        reporting: "Reporting Manager",
                        project: "Project Manager",
                        qa: "QA Manager",
                        product: "Product Manager",
                      };
                      const fmt = (d: string | null) =>
                        d ? new Date(d).toLocaleDateString("en-GB") : "dd/mm/yyyy";
                      const managersToShow = managerAssignments.slice(0, 2);

                      // Group direct reports by manager level (M1 / M2)
                      const m1Reports = managedEmployees.filter((e) => e.manager_level === "M1");
                      const m2Reports = managedEmployees.filter((e) => e.manager_level === "M2");

                      const hasNoManagers = managersToShow.length === 0;
                      const hasNoDirectReports = !isActiveManager || managedEmployees.length === 0;

                      return (
                        <>
                          {/* Fallback when no managers are assigned */}
                          {hasNoManagers && hasNoDirectReports && (
                            <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
                              No managers assigned
                            </Typography>
                          )}

                          {/* My own managers */}
                          {managersToShow.map((a, idx) => (
                            <Box
                              key={"id" in a ? a.id : `global-${idx}`}
                              sx={{
                                py: 1,
                                borderBottom: idx < managersToShow.length - 1 ? "1px solid #f0f0f0" : "none",
                              }}
                            >
                              <Typography>
                                {a.assignment_type
                                  ? typeLabel[a.assignment_type] ?? `${a.manager_level} Manager`
                                  : `${a.manager_level} Manager`}
                                : <strong>{a.manager_name || "N/A"}</strong>
                              </Typography>
                              {"from_date" in a && (a.from_date || a.till_date) && (
                                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                                  From: {fmt(a.from_date)} &nbsp; To: {fmt(a.till_date)}
                                </Typography>
                              )}
                            </Box>
                          ))}

                          {/* Direct reportees — only shown when actively using Manager role */}
                          {isActiveManager && managedEmployees.length > 0 && (
                            <Box sx={{ mt: managersToShow.length > 0 ? 1.5 : 0 }}>
                              {managersToShow.length > 0 && (
                                <Divider sx={{ mb: 1.5, borderStyle: "dashed" }} />
                              )}
                              <Typography sx={{ fontWeight: "bold", color: "#007300" }}>
                                Direct Reportees
                              </Typography>
                              <Typography sx={{ mt: 0.75 }}>
                                {Array.from(
                                  new Map(managedEmployees.map((r) => [r.employee_name, r])).values()
                                ).map((r, idx, arr) => (
                                  <Typography key={r.id} component="span">
                                    <strong>{r.employee_name}</strong>
                                    {idx < arr.length - 1 && ", "}
                                  </Typography>
                                ))}
                              </Typography>
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                ) : (
                  <Typography>Loading Manager Data...</Typography>
                )}
              </Box>
            </Box>

          </CardContent>
        </Card>

        {/* Quick Links Section */}
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              sx={{ color: "#007300", mb: 3 }}
            >
              Quick Links
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{ flexWrap: "wrap", gap: 2, justifyContent: "center" }}
            >
              {quickLinks.map((link) => (
                <Button
                  key={link.id}
                  variant="contained"
                  href={link.path}
                  sx={{
                    backgroundColor: "#ebebeb",
                    color: "#007300",
                    "&:hover": {
                      backgroundColor: "#007300",
                      color: "#fff",
                    },
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: 2,
                    width: "100%",
                    maxWidth: "300px",
                  }}
                >
                  <Box sx={{ mr: 2, display: "flex", alignItems: "center" }}>
                    {link.icon}
                  </Box>
                  <Typography variant="body1" fontWeight="bold">
                    {link.title}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {link.description}
                  </Typography>
                </Button>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Box>
      <Footer />

    </>
  );
};

export default LandingPage;
